// src/runtime/KnowledgeEvolutionRuntime/extractor.ts
// ============================================
// Knowledge Extractor - 知识提取器
//
// 职责：从 Hermes 对话 / Runtime 日志 / 审核反馈 / Git Commit / 客户跟进记录中
//       自动提取候选知识（CandidateKnowledge），写入 30_Inbox/。
//
// 设计原则：
//   1. 启发式提取（规则 + 关键词），不依赖 LLM，保证在浏览器/Node 环境都能运行
//   2. 只提取"有信息量"的句子（排除问候、寒暄、无意义噪声）
//   3. 每条候选知识都标注来源（source + sourceRef），满足 Rule 04 可追溯
//   4. 价值评分 0-100：信息密度 + 关键词命中 + 长度
// ============================================

import {
  KnowledgeSourceType,
  type CandidateKnowledge,
} from './types';
import config from './config';

/** 原始知识来源（未处理） */
export interface RawKnowledgeSource {
  /** 来源类型 */
  source: KnowledgeSourceType;
  /** 来源引用（对话 ID / 日志路径 / commit hash / 客户 ID） */
  ref: string;
  /** 原始文本 */
  text: string;
}

/** 无价值噪声关键词（命中则跳过该句） */
const NOISE_KEYWORDS = [
  '你好',
  '请问',
  '好的',
  '收到',
  '谢谢',
  '再见',
  '哈喽',
  '测试',
  'test',
  'hello',
  'hi',
  '嗯嗯',
  '稍等',
  'OK',
];

/** 高价值信号关键词（命中则提升评分） */
const VALUE_SIGNAL_KEYWORDS = [
  '经验',
  '教训',
  '总结',
  '规律',
  '方法',
  '技巧',
  '最佳实践',
  '踩坑',
  '发现',
  '结论',
  '决定',
  '规则',
  '流程',
  'sop',
  'SOP',
  '策略',
  '方案',
  '案例',
  '反馈',
  '问题',
  '原因',
  '效果',
  '验证',
  '成功',
  '失败',
];

export class KnowledgeExtractor {
  /**
   * 从原始来源批量提取候选知识
   *
   * @param sources 原始知识来源列表
   * @param baseDate 提取日期（YYYY-MM-DD），用于生成 ID
   */
  extractAll(
    sources: RawKnowledgeSource[],
    baseDate: string
  ): CandidateKnowledge[] {
    const candidates: CandidateKnowledge[] = [];
    for (const raw of sources) {
      const chunks = this.splitIntoSentences(raw.text);
      for (const chunk of chunks) {
        const clean = this.cleanSentence(chunk);
        if (!this.isMeaningful(clean)) continue;
        const score = this.scoreSentence(clean);
        if (score < 40) continue;
        candidates.push(
          this.buildCandidate(raw, clean, score, baseDate, candidates.length)
        );
        if (candidates.length >= config.maxCandidatesPerRun) break;
      }
    }
    return this.dedupe(candidates);
  }

  /** 将文本切分为候选句子（按中文标点 / 换行 / 英文句号切分） */
  private splitIntoSentences(text: string): string[] {
    if (!text) return [];
    return text
      .split(/\n+|[。！？!?；;]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  /** 清理句子（去空白、去引用标记、去无意义前缀） */
  private cleanSentence(s: string): string {
    return s
      .replace(/^[-*#>\s]+/, '')
      .replace(/\s+/g, ' ')
      .replace(/^[\[(【][^\]】)]*[\]】)]\s*/, '')
      .trim();
  }

  /** 判断句子是否有信息量 */
  private isMeaningful(s: string): boolean {
    if (s.length < config.minSentenceLength) return false;
    const lower = s.toLowerCase();
    if (NOISE_KEYWORDS.some((k) => lower.includes(k.toLowerCase()))) {
      // 噪声词同时是完整句子（非嵌入句）才过滤
      if (s.length < 30) return false;
    }
    // 过滤纯链接 / 纯时间戳 / 纯数字
    if (/^(https?:\/\/|\[|【|\d{4}-\d{2}-\d{2}|[\d:.-]{6,})/.test(s)) return false;
    // 过滤命令式日志行（如 `[INFO] ...`）
    if (/^\[(INFO|WARN|ERROR|DEBUG|LOG)\]/i.test(s)) return false;
    return true;
  }

  /** 价值评分 0-100 */
  private scoreSentence(s: string): number {
    let score = 40;
    // 信息密度：含数字/百分比/专有名词提升
    const numberHits = (s.match(/\d+/g) || []).length;
    score += Math.min(10, numberHits * 2);
    // 关键词信号
    const signalHits = VALUE_SIGNAL_KEYWORDS.filter((k) =>
      s.includes(k)
    ).length;
    score += Math.min(30, signalHits * 6);
    // 长度：适中（30-120 字）信息量最高
    if (s.length >= 30 && s.length <= 120) score += 15;
    else if (s.length > 120) score += 8;
    // 含结论性词汇加分
    if (/(因此|所以|建议|应该|必须|避免|记得|关键|核心)/.test(s)) score += 5;
    return Math.min(100, score);
  }

  /** 构建候选知识 */
  private buildCandidate(
    raw: RawKnowledgeSource,
    sentence: string,
    score: number,
    baseDate: string,
    index: number
  ): CandidateKnowledge {
    const id = this.slugify(`${baseDate}-${raw.source}-${index}-${sentence.slice(0, 16)}`);
    const summary = this.makeSummary(sentence);
    return {
      id,
      title: sentence.slice(0, 24) + (sentence.length > 24 ? '…' : ''),
      summary,
      content: sentence,
      source: raw.source,
      sourceRef: raw.ref,
      riskLevel: 'low', // 风险等级由 Validator 评估
      status: 'pending',
      detectedAt: new Date().toISOString(),
      targetCategory: '待分类',
      tags: this.guessTags(sentence),
      score: Math.round(score),
    };
  }

  /** 生成摘要（取第一句 + 关键词） */
  private makeSummary(sentence: string): string {
    const first = sentence.slice(0, 40);
    return first + (sentence.length > 40 ? '…' : '');
  }

  /** 简单标签猜测（按关键词归类） */
  private guessTags(sentence: string): string[] {
    const tags: string[] = [];
    const map: Array<[string, string[]]> = [
      ['客户', ['客户', 'CRM', '成交', '报价', '签约', '跟进', '线索']],
      ['营销', ['营销', '内容', '发布', '公众号', '小红书', '抖音', '广告']],
      ['产品', ['产品', 'SaaS', 'Agent', '工具', '功能', '上线']],
      ['AI/LLM', ['LLM', '模型', 'Prompt', '知识库', 'RAG', 'AI']],
      ['财务', ['收入', '成本', '利润', '现金流', '预算', '定价']],
      ['管理', ['流程', '制度', '复盘', '团队', '招聘', '协作']],
    ];
    for (const [tag, words] of map) {
      if (words.some((w) => sentence.includes(w))) tags.push(tag);
    }
    return tags;
  }

  /** 去重：同来源 + 相似句子（前 20 字相同视为重复） */
  private dedupe(candidates: CandidateKnowledge[]): CandidateKnowledge[] {
    const seen = new Set<string>();
    return candidates.filter((c) => {
      const key = `${c.source}|${c.content.slice(0, 20)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  /** 文件名 slug 化 */
  private slugify(s: string): string {
    return s
      .toLowerCase()
      .replace(/[^a-z0-9\u4e00-\u9fa5]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || `candidate-${Date.now()}`;
  }
}

export default KnowledgeExtractor;
